import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Trash2, Send, Download, FileText, Landmark, Percent, Users, Mail, FileBarChart } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import api from "@/lib/api";

const fmtX = (v) => (v == null ? "-" : `${v}x`);
const fmtPct = (v) => (v == null ? "-" : `${(v * 100).toFixed(1)}%`);

function MetricCards({ m }) {
  const cards = [
    ["Committed", formatCurrency(m.committed)], ["Called", formatCurrency(m.called)],
    ["Paid-in", formatCurrency(m.contributed)], ["Uncalled", formatCurrency(m.uncalled)],
    ["Distributed", formatCurrency(m.distributed)], ["NAV", formatCurrency(m.nav)],
    ["Net IRR", fmtPct(m.irr)], ["TVPI", fmtX(m.tvpi)], ["DPI", fmtX(m.dpi)],
    ["RVPI", fmtX(m.rvpi)], ["MOIC", fmtX(m.moic)], ["Holdings", formatCurrency(m.holdings_value)],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="fund-metric-cards">
      {cards.map(([l, v]) => (
        <Card key={l} className="rounded-xl border p-4">
          <div className="text-sm font-bold text-foreground sm:text-base">{v}</div>
          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{l}</div>
        </Card>
      ))}
    </div>
  );
}

function LpsTab({ fund }) {
  const [lps, setLps] = useState([]);
  const EMPTY = { name: "", email: "", entity_type: "individual", commitment: "", status: "committed", sl_fee: "", sl_carry: "", sl_note: "", send_invite: false };
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/funds/${fund.id}/lps`).then(({ data }) => setLps(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fund.id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    const sl = form.sl_fee !== "" || form.sl_carry !== "" || form.sl_note
      ? { mgmt_fee_pct: form.sl_fee === "" ? null : Number(form.sl_fee), carry_pct: form.sl_carry === "" ? null : Number(form.sl_carry), note: form.sl_note } : null;
    const body = { name: form.name, email: form.email, entity_type: form.entity_type, commitment: Number(form.commitment) || 0, status: form.status, side_letter: sl, send_invite: !editId && form.send_invite };
    try {
      if (editId) await api.put(`/fund-lps/${editId}`, body);
      else await api.post(`/funds/${fund.id}/lps`, body);
      toast.success(editId ? "LP updated" : form.send_invite ? "LP added - invite email sent" : "LP added");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const edit = (lp) => {
    setForm({ name: lp.name, email: lp.email, entity_type: lp.entity_type, commitment: lp.commitment, status: lp.status,
      sl_fee: lp.side_letter?.mgmt_fee_pct ?? "", sl_carry: lp.side_letter?.carry_pct ?? "", sl_note: lp.side_letter?.note || "", send_invite: false });
    setEditId(lp.id); setOpen(true);
  };
  const sendInvite = async (lp) => {
    try { await api.post(`/fund-lps/${lp.id}/invite`); toast.success(`Invite emailed to ${lp.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send invite"); }
  };
  const remove = async (lp) => {
    try { await api.delete(`/fund-lps/${lp.id}`); toast.success("LP removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot remove"); }
  };
  const statement = async (lp) => {
    try {
      const res = await api.get(`/fund-lps/${lp.id}/statement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `capital-account-${lp.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Statement download failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-lp-btn"><Plus className="h-4 w-4" /> Add LP</Button></div>
      {lps.length === 0 ? <Card className="rounded-xl border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="lps-empty">No LPs yet - add your first limited partner and their commitment.</Card> : (
        <Card className="overflow-x-auto rounded-xl border p-0" data-testid="lps-table">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              {["LP", "Commitment", "Called", "Paid-in", "Distributed", "Side letter", ""].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {lps.map((lp) => (
                <tr key={lp.id} className="border-b last:border-0" data-testid={`lp-row-${lp.id}`}>
                  <td className="p-3"><div className="font-medium text-foreground">{lp.name}</div><div className="text-xs text-muted-foreground">{lp.email} · {lp.entity_type}</div></td>
                  <td className="p-3 font-medium">{formatCurrency(lp.commitment)}</td>
                  <td className="p-3">{formatCurrency(lp.called)}</td>
                  <td className="p-3">{formatCurrency(lp.contributed)}</td>
                  <td className="p-3">{formatCurrency(lp.distributed)}</td>
                  <td className="p-3 text-xs">{lp.side_letter ? <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">{lp.side_letter.mgmt_fee_pct != null ? `${lp.side_letter.mgmt_fee_pct}% fee ` : ""}{lp.side_letter.carry_pct != null ? `${lp.side_letter.carry_pct}% carry` : ""}</span> : "-"}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => sendInvite(lp)} title="Email portal invite" data-testid={`lp-invite-${lp.id}`}><Mail className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => statement(lp)} title="Capital account statement" data-testid={`lp-statement-${lp.id}`}><FileText className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => edit(lp)} data-testid={`lp-edit-${lp.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(lp)} data-testid={`lp-delete-${lp.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="lp-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit LP" : "Add limited partner"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="lp-form-name" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="lp-form-email" /></div>
            <div><Label>Entity type</Label>
              <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                <SelectTrigger className="mt-1" data-testid="lp-form-entity"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="individual">Individual</SelectItem><SelectItem value="entity">Entity / Fund</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Commitment ($)</Label><Input type="number" value={form.commitment} onChange={(e) => set("commitment", e.target.value)} className="mt-1" data-testid="lp-form-commitment" /></div>
            <div className="col-span-2 rounded-lg border border-dashed p-3">
              <Label className="text-xs font-semibold">Side letter overrides (optional)</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Mgmt fee % (default {fund.mgmt_fee_pct}%)</Label><Input type="number" step="0.1" value={form.sl_fee} onChange={(e) => set("sl_fee", e.target.value)} className="mt-1 h-8" data-testid="lp-form-sl-fee" /></div>
                <div><Label className="text-xs">Carry % (default {fund.carry_pct}%)</Label><Input type="number" step="0.5" value={form.sl_carry} onChange={(e) => set("sl_carry", e.target.value)} className="mt-1 h-8" data-testid="lp-form-sl-carry" /></div>
              </div>
              <Input value={form.sl_note} onChange={(e) => set("sl_note", e.target.value)} placeholder="Side letter note" className="mt-2 h-8" data-testid="lp-form-sl-note" />
            </div>
            {!editId && (
              <label className="col-span-2 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={form.send_invite} onCheckedChange={(v) => set("send_invite", !!v)} data-testid="lp-form-send-invite" />
                Email this LP an invite to their portal
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="lp-form-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save LP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CallsTab({ fund, onChanged }) {
  const [calls, setCalls] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ purpose: "", mode: "pct", pct: "", amount: "", due_date: "", wire: "" });
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/funds/${fund.id}/capital-calls`).then(({ data }) => setCalls(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fund.id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.due_date) { toast.error("Set a due date"); return; }
    setBusy("create");
    try {
      await api.post(`/funds/${fund.id}/capital-calls`, {
        purpose: form.purpose, due_date: form.due_date, wire_instructions: form.wire,
        pct_of_commitment: form.mode === "pct" ? Number(form.pct) || null : null,
        amount: form.mode === "amount" ? Number(form.amount) || null : null,
      });
      toast.success("Capital call drafted");
      setOpen(false); setForm({ purpose: "", mode: "pct", pct: "", amount: "", due_date: "", wire: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create call"); }
    finally { setBusy(""); }
  };
  const issue = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await api.post(`/capital-calls/${c.id}/issue`);
      toast.success(`Call #${c.call_number} issued - ${data.notices_sent} notice(s) emailed`);
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setBusy(""); }
  };
  const markPaid = async (item, paid) => {
    try {
      await api.put(`/capital-call-items/${item.id}`, { status: paid ? "paid" : "pending" });
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const removeDraft = async (c) => {
    try { await api.delete(`/capital-calls/${c.id}`); toast.success("Draft deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="new-call-btn"><Plus className="h-4 w-4" /> New capital call</Button></div>
      {calls.length === 0 && <Card className="rounded-xl border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="calls-empty">No capital calls yet. Draft one and tenx computes each LP's pro-rata share.</Card>}
      {calls.map((c) => (
        <Card key={c.id} className="rounded-xl border p-5" data-testid={`call-card-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">Call #{c.call_number} - {formatCurrency(c.amount)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c.status === "draft" ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{c.status}</span>
              </div>
              <div className="text-xs text-muted-foreground">Due {c.due_date}{c.purpose ? ` · ${c.purpose}` : ""} · collected {formatCurrency(c.paid_total)} of {formatCurrency(c.amount)}</div>
            </div>
            <div className="flex gap-2">
              {c.status === "draft" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeDraft(c)} data-testid={`call-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
              {c.status === "draft" && <Button size="sm" className="gap-1.5" onClick={() => issue(c)} disabled={busy === c.id} data-testid={`call-issue-${c.id}`}>{busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Issue & notify LPs</Button>}
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {c.items.map((i) => (
              <div key={i.id} className="flex items-center justify-between rounded-lg border p-2.5 text-xs" data-testid={`call-item-${i.id}`}>
                <div><span className="font-medium text-foreground">{i.lp_name}</span> <span className="text-muted-foreground">· {formatCurrency(i.amount)}</span>
                          {i.lp_marked_at && i.status !== "paid" && <span data-testid={`lp-marked-sent-${i.id}`} className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">LP says sent</span>}
                </div>
                {c.status !== "draft" && (i.status === "paid"
                  ? <Button size="sm" variant="ghost" className="h-7 text-[11px] text-emerald-700" onClick={() => markPaid(i, false)} data-testid={`call-item-unpay-${i.id}`}>Paid ✓ (undo)</Button>
                  : <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => markPaid(i, true)} data-testid={`call-item-pay-${i.id}`}>Mark paid</Button>)}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="call-dialog">
          <DialogHeader><DialogTitle>New capital call</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="Initial closing / Follow-on into DealCo" className="mt-1" data-testid="call-form-purpose" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Basis</Label>
                <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                  <SelectTrigger className="mt-1" data-testid="call-form-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pct">% of commitments</SelectItem><SelectItem value="amount">Fixed total $</SelectItem></SelectContent>
                </Select>
              </div>
              {form.mode === "pct"
                ? <div><Label>% to call</Label><Input type="number" value={form.pct} onChange={(e) => set("pct", e.target.value)} placeholder="25" className="mt-1" data-testid="call-form-pct" /></div>
                : <div><Label>Total amount ($)</Label><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1" data-testid="call-form-amount" /></div>}
            </div>
            <div><Label>Due date <span className="text-red-500">*</span></Label><DatePicker value={form.due_date} onChange={(v) => set("due_date", v)} className="mt-1" data-testid="call-form-due" /></div>
            <div><Label>Wire / payment instructions</Label><Textarea rows={2} value={form.wire} onChange={(e) => set("wire", e.target.value)} placeholder="Bank, account, reference…" className="mt-1" data-testid="call-form-wire" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy === "create"} className="gap-2" data-testid="call-form-save">{busy === "create" && <Loader2 className="h-4 w-4 animate-spin" />} Draft call</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvestmentsTab({ fund, onChanged }) {
  const [invs, setInvs] = useState([]);
  const [open, setOpen] = useState(false);
  const [markFor, setMarkFor] = useState(null);
  const [exitFor, setExitFor] = useState(null);
  const [form, setForm] = useState({ company_name: "", invested_on: "", cost: "", security_type: "Equity", note: "" });
  const [mark, setMark] = useState({ as_of: "", value: "", note: "" });
  const [exitVal, setExitVal] = useState({ realized_proceeds: "", status: "exited" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/funds/${fund.id}/investments`).then(({ data }) => setInvs(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fund.id]);
  const add = async () => {
    setSaving(true);
    try {
      await api.post(`/funds/${fund.id}/investments`, { ...form, cost: Number(form.cost) || 0 });
      toast.success("Investment recorded"); setOpen(false);
      setForm({ company_name: "", invested_on: "", cost: "", security_type: "Equity", note: "" });
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const saveMark = async () => {
    setSaving(true);
    try {
      await api.post(`/fund-investments/${markFor.id}/marks`, { ...mark, value: Number(mark.value) || 0 });
      toast.success("Valuation mark recorded"); setMarkFor(null); setMark({ as_of: "", value: "", note: "" });
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const saveExit = async () => {
    setSaving(true);
    try {
      await api.post(`/fund-investments/${exitFor.id}/exit`, { realized_proceeds: Number(exitVal.realized_proceeds) || 0, status: exitVal.status });
      toast.success("Exit recorded"); setExitFor(null); setExitVal({ realized_proceeds: "", status: "exited" });
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (inv) => {
    try { await api.delete(`/fund-investments/${inv.id}`); toast.success("Removed"); load(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="add-investment-btn"><Plus className="h-4 w-4" /> Record investment</Button></div>
      {invs.length === 0 ? <Card className="rounded-xl border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="investments-empty">No holdings yet. Record deployments to power NAV, TVPI and the Schedule of Investments.</Card> : (
        <Card className="overflow-x-auto rounded-xl border p-0" data-testid="investments-table">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              {["Company", "Invested", "Cost", "Fair value", "MOIC", "Status", ""].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {invs.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0" data-testid={`investment-row-${inv.id}`}>
                  <td className="p-3"><div className="font-medium text-foreground">{inv.company_name}</div><div className="text-xs text-muted-foreground">{inv.security_type}{inv.marks?.length ? ` · last mark ${inv.marks[0].as_of}` : ""}</div></td>
                  <td className="p-3 text-xs">{inv.invested_on}</td>
                  <td className="p-3">{formatCurrency(inv.cost)}</td>
                  <td className="p-3 font-medium">{inv.status === "active" ? formatCurrency(inv.current_value) : formatCurrency(inv.realized_proceeds)}</td>
                  <td className="p-3">{inv.cost ? `${(((inv.status === "active" ? inv.current_value : 0) + (inv.realized_proceeds || 0)) / inv.cost).toFixed(2)}x` : "-"}</td>
                  <td className="p-3"><span data-testid={`investment-status-${inv.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${inv.status === "active" ? "bg-emerald-50 text-emerald-700" : inv.status === "exited" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{inv.status}</span></td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      {inv.status === "active" && <Button size="sm" variant="ghost" onClick={() => { setMarkFor(inv); setMark({ as_of: "", value: inv.current_value, note: "" }); }} data-testid={`investment-mark-${inv.id}`}>Mark</Button>}
                      {inv.status === "active" && <Button size="sm" variant="ghost" onClick={() => setExitFor(inv)} data-testid={`investment-exit-${inv.id}`}>Exit</Button>}
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(inv)} data-testid={`investment-delete-${inv.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="investment-dialog">
          <DialogHeader><DialogTitle>Record investment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Company / asset</Label><Input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} className="mt-1" data-testid="investment-form-company" /></div>
            <div><Label>Date</Label><DatePicker value={form.invested_on} onChange={(v) => setForm((f) => ({ ...f, invested_on: v }))} className="mt-1" data-testid="investment-form-date" /></div>
            <div><Label>Cost ($)</Label><Input type="number" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} className="mt-1" data-testid="investment-form-cost" /></div>
            <div><Label>Security</Label><Input value={form.security_type} onChange={(e) => setForm((f) => ({ ...f, security_type: e.target.value }))} placeholder="Equity / SAFE / Note" className="mt-1" data-testid="investment-form-security" /></div>
            <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="mt-1" data-testid="investment-form-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} disabled={saving} className="gap-2" data-testid="investment-form-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!markFor} onOpenChange={(v) => !v && setMarkFor(null)}>
        <DialogContent data-testid="mark-dialog">
          <DialogHeader><DialogTitle>Valuation mark - {markFor?.company_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>As of</Label><DatePicker value={mark.as_of} onChange={(v) => setMark((m) => ({ ...m, as_of: v }))} className="mt-1" data-testid="mark-form-date" /></div>
            <div><Label>Fair value ($)</Label><Input type="number" value={mark.value} onChange={(e) => setMark((m) => ({ ...m, value: e.target.value }))} className="mt-1" data-testid="mark-form-value" /></div>
            <div className="col-span-2"><Label>Note</Label><Input value={mark.note} onChange={(e) => setMark((m) => ({ ...m, note: e.target.value }))} placeholder="Series B priced round…" className="mt-1" data-testid="mark-form-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkFor(null)}>Cancel</Button>
            <Button onClick={saveMark} disabled={saving} className="gap-2" data-testid="mark-form-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save mark</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!exitFor} onOpenChange={(v) => !v && setExitFor(null)}>
        <DialogContent data-testid="exit-dialog">
          <DialogHeader><DialogTitle>Exit - {exitFor?.company_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Proceeds ($)</Label><Input type="number" value={exitVal.realized_proceeds} onChange={(e) => setExitVal((x) => ({ ...x, realized_proceeds: e.target.value }))} className="mt-1" data-testid="exit-form-proceeds" /></div>
            <div><Label>Outcome</Label>
              <Select value={exitVal.status} onValueChange={(v) => setExitVal((x) => ({ ...x, status: v }))}>
                <SelectTrigger className="mt-1" data-testid="exit-form-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="exited">Exited</SelectItem><SelectItem value="written_off">Written off</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExitFor(null)}>Cancel</Button>
            <Button onClick={saveExit} disabled={saving} className="gap-2" data-testid="exit-form-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Record exit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DistributionsTab({ fund, onChanged }) {
  const [dists, setDists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", date: "", source: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/funds/${fund.id}/distributions`).then(({ data }) => setDists(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fund.id]);
  const runPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post(`/funds/${fund.id}/distributions/preview`, { ...form, amount: Number(form.amount) || 0, date: form.date || new Date().toISOString().slice(0, 10) });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      await api.post(`/funds/${fund.id}/distributions`, { ...form, amount: Number(form.amount) || 0, date: form.date || new Date().toISOString().slice(0, 10) });
      toast.success("Distribution drafted");
      setOpen(false); setForm({ amount: "", date: "", source: "" }); setPreview(null);
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };
  const issue = async (d) => {
    setBusy(d.id);
    try {
      const { data } = await api.post(`/distributions/${d.id}/issue`);
      toast.success(`Distribution #${d.dist_number} issued - ${data.notices_sent} notice(s) emailed`);
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setBusy(""); }
  };
  const removeDraft = async (d) => {
    try { await api.delete(`/distributions/${d.id}`); toast.success("Draft deleted"); load(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const wfRow = (wf) => `ROC ${formatCurrency(wf.return_of_capital)} · Pref ${formatCurrency(wf.pref_return)} · LP profit ${formatCurrency(wf.lp_profit)} · GP carry ${formatCurrency(wf.gp_carry)}`;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => { setPreview(null); setOpen(true); }} data-testid="new-distribution-btn"><Plus className="h-4 w-4" /> New distribution</Button></div>
      {dists.length === 0 && <Card className="rounded-xl border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="distributions-empty">No distributions yet. tenx runs a European waterfall - return of capital, preferred return, then carry.</Card>}
      {dists.map((d) => (
        <Card key={d.id} className="rounded-xl border p-5" data-testid={`dist-card-${d.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">Distribution #{d.dist_number} - {formatCurrency(d.amount)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${d.status === "draft" ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{d.status}</span>
              </div>
              <div className="text-xs text-muted-foreground">{d.date}{d.source ? ` · ${d.source}` : ""} · {wfRow(d.waterfall)}</div>
            </div>
            <div className="flex gap-2">
              {d.status === "draft" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeDraft(d)} data-testid={`dist-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
              {d.status === "draft" && <Button size="sm" className="gap-1.5" onClick={() => issue(d)} disabled={busy === d.id} data-testid={`dist-issue-${d.id}`}>{busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Issue & notify LPs</Button>}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {d.items.map((i) => (
              <div key={i.id} className="flex items-center justify-between rounded-lg border p-2.5 text-xs" data-testid={`dist-item-${i.id}`}>
                <span className="font-medium text-foreground">{i.lp_name}</span>
                <span className="text-emerald-700">{formatCurrency(i.amount)}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="distribution-dialog">
          <DialogHeader><DialogTitle>New distribution</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Amount ($)</Label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1" data-testid="dist-form-amount" /></div>
            <div><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} className="mt-1" data-testid="dist-form-date" /></div>
            <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} placeholder="Exit: DealCo" className="mt-1" data-testid="dist-form-source" /></div>
          </div>
          <Button variant="outline" size="sm" onClick={runPreview} disabled={busy === "preview" || !Number(form.amount)} className="gap-2" data-testid="dist-preview-btn">{busy === "preview" && <Loader2 className="h-4 w-4 animate-spin" />} Preview waterfall</Button>
          {preview && (
            <div className="rounded-lg border bg-secondary/40 p-3 text-xs" data-testid="dist-preview">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["Return of capital", preview.waterfall.return_of_capital], ["Preferred return", preview.waterfall.pref_return], ["LP profit", preview.waterfall.lp_profit], ["GP carry", preview.waterfall.gp_carry]].map(([l, v]) => (
                  <div key={l}><div className="font-bold text-foreground">{formatCurrency(v)}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>
                ))}
              </div>
              <div className="mt-2 max-h-40 space-y-1 overflow-y-auto border-t pt-2">
                {preview.allocations.map((a) => (
                  <div key={a.lp_id} className="flex justify-between"><span>{a.lp_name} ({a.share_pct}%)</span><span className="font-medium">{formatCurrency(a.amount)}</span></div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy === "save"} className="gap-2" data-testid="dist-form-save">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Draft distribution</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeesCarryTab({ fund }) {
  const year = new Date().getFullYear();
  const [feeYear, setFeeYear] = useState(year);
  const [schedule, setSchedule] = useState(null);
  const [events, setEvents] = useState([]);
  const [grants, setGrants] = useState([]);
  const [openGrant, setOpenGrant] = useState(false);
  const [gform, setGform] = useState({ member_name: "", email: "", carry_share_pct: "", start_date: "", vest_years: 4, cliff_months: 12 });
  const [busy, setBusy] = useState("");
  const load = () => {
    api.get(`/funds/${fund.id}/fee-schedule`, { params: { year: feeYear } }).then(({ data }) => setSchedule(data)).catch(() => {});
    api.get(`/funds/${fund.id}/fee-events`).then(({ data }) => setEvents(data)).catch(() => {});
    api.get(`/funds/${fund.id}/carry-grants`).then(({ data }) => setGrants(data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fund.id, feeYear]);
  const record = async () => {
    setBusy("fee");
    try { await api.post(`/funds/${fund.id}/fee-events?year=${feeYear}`); toast.success(`${feeYear} management fees recorded`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const addGrant = async () => {
    setBusy("grant");
    try {
      await api.post(`/funds/${fund.id}/carry-grants`, { ...gform, carry_share_pct: Number(gform.carry_share_pct) || 0, vest_years: Number(gform.vest_years) || 4, cliff_months: Number(gform.cliff_months) || 0 });
      toast.success("Carry grant added"); setOpenGrant(false);
      setGform({ member_name: "", email: "", carry_share_pct: "", start_date: "", vest_years: 4, cliff_months: 12 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const removeGrant = async (g) => {
    try { await api.delete(`/carry-grants/${g.id}`); toast.success("Grant removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card className="rounded-xl border p-5" data-testid="fee-schedule-card">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold text-foreground"><Percent className="h-4 w-4 text-primary" /> Management fees</h3>
          <div className="flex items-center gap-2">
            <Input type="number" value={feeYear} onChange={(e) => setFeeYear(Number(e.target.value) || year)} className="h-8 w-24" data-testid="fee-year-input" />
            <Button size="sm" onClick={record} disabled={busy === "fee" || !!schedule?.recorded} data-testid="record-fees-btn">{schedule?.recorded ? "Recorded ✓" : busy === "fee" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record year"}</Button>
          </div>
        </div>
        {schedule && (
          <>
            <div className="mt-3 space-y-1.5">
              {schedule.items.map((i) => (
                <div key={i.lp_id} className="flex items-center justify-between rounded-lg border p-2.5 text-xs" data-testid={`fee-item-${i.lp_id}`}>
                  <span>{i.lp_name} <span className="text-muted-foreground">· {i.rate_pct}% of {formatCurrency(i.commitment)}</span></span>
                  <span className="font-medium">{formatCurrency(i.amount)}</span>
                </div>
              ))}
              {schedule.items.length === 0 && <p className="text-xs text-muted-foreground">Add LPs to compute the fee schedule.</p>}
            </div>
            <div className="mt-2 flex justify-between border-t pt-2 text-sm font-semibold"><span>Total {feeYear} fees</span><span data-testid="fee-total">{formatCurrency(schedule.total)}</span></div>
          </>
        )}
        {events.length > 0 && <p className="mt-3 text-xs text-muted-foreground">Recorded: {events.map((ev) => `${ev.year} (${formatCurrency(ev.total)})`).join(" · ")}</p>}
      </Card>
      <Card className="rounded-xl border p-5" data-testid="carry-grants-card">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold text-foreground"><Users className="h-4 w-4 text-primary" /> GP carry grants</h3>
          <Button size="sm" className="gap-1.5" onClick={() => setOpenGrant(true)} data-testid="add-carry-grant-btn"><Plus className="h-4 w-4" /> Grant</Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Split the {fund.carry_pct}% carry pool across your GP team with vesting.</p>
        <div className="mt-3 space-y-1.5">
          {grants.map((g) => (
            <div key={g.id} className="flex items-center justify-between rounded-lg border p-2.5 text-xs" data-testid={`carry-grant-${g.id}`}>
              <div>
                <span className="font-medium text-foreground">{g.member_name}</span>
                <span className="text-muted-foreground"> · {g.carry_share_pct}% of pool · {g.vest_years}y vest / {g.cliff_months}m cliff</span>
              </div>
              <div className="flex items-center gap-2">
                        <span data-testid={`grant-vested-${g.id}`} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">{g.vested_pct}% vested</span>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => removeGrant(g)} data-testid={`carry-grant-delete-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
          {grants.length === 0 && <p className="text-xs text-muted-foreground">No grants yet.</p>}
        </div>
      </Card>
      <Dialog open={openGrant} onOpenChange={setOpenGrant}>
        <DialogContent data-testid="carry-grant-dialog">
          <DialogHeader><DialogTitle>New carry grant</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Member</Label><Input value={gform.member_name} onChange={(e) => setGform((f) => ({ ...f, member_name: e.target.value }))} className="mt-1" data-testid="grant-form-name" /></div>
            <div><Label>Email</Label><Input value={gform.email} onChange={(e) => setGform((f) => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="grant-form-email" /></div>
            <div><Label>% of carry pool</Label><Input type="number" value={gform.carry_share_pct} onChange={(e) => setGform((f) => ({ ...f, carry_share_pct: e.target.value }))} className="mt-1" data-testid="grant-form-share" /></div>
            <div><Label>Start date</Label><DatePicker value={gform.start_date} onChange={(v) => setGform((f) => ({ ...f, start_date: v }))} className="mt-1" data-testid="grant-form-start" /></div>
            <div><Label>Vest years</Label><Input type="number" value={gform.vest_years} onChange={(e) => setGform((f) => ({ ...f, vest_years: e.target.value }))} className="mt-1" data-testid="grant-form-vest" /></div>
            <div><Label>Cliff months</Label><Input type="number" value={gform.cliff_months} onChange={(e) => setGform((f) => ({ ...f, cliff_months: e.target.value }))} className="mt-1" data-testid="grant-form-cliff" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenGrant(false)}>Cancel</Button>
            <Button onClick={addGrant} disabled={busy === "grant"} className="gap-2" data-testid="grant-form-save">{busy === "grant" && <Loader2 className="h-4 w-4 animate-spin" />} Add grant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocsTab({ fund }) {
  const [docs, setDocs] = useState([]);
  const [lps, setLps] = useState([]);
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [category, setCategory] = useState("other");
  const [lpId, setLpId] = useState("all");
  const [uploading, setUploading] = useState(false);
  const load = () => {
    api.get(`/funds/${fund.id}/docs`).then(({ data }) => setDocs(data)).catch(() => {});
    api.get(`/funds/${fund.id}/lps`).then(({ data }) => setLps(data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fund.id]);
  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (lpId !== "all") fd.append("lp_id", lpId);
    try {
      await api.post(`/funds/${fund.id}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setFile(null); setFileKey((k) => k + 1); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/fund-docs/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.name || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const remove = async (d) => {
    try { await api.delete(`/fund-docs/${d.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const CATS = [["k1", "K-1"], ["call_notice", "Call notice"], ["dist_notice", "Distribution notice"], ["lpa", "LPA"], ["report", "Report"], ["other", "Other"]];
  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-end gap-3 rounded-xl border p-4" data-testid="fund-doc-upload">
        <div className="min-w-52 flex-1"><Label className="text-xs">File (K-1, LPA, report…)</Label>
          <Input key={fileKey} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.png,.jpg,.jpeg,.gif,.webp,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="fund-doc-file" />
          <p className="mt-1 text-[11px] text-muted-foreground" data-testid="fund-doc-type-hint">PDF, Office files, images &amp; spreadsheets · max 25MB</p></div>
        <div><Label className="text-xs">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-1 w-40" data-testid="fund-doc-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Visible to</Label>
          <Select value={lpId} onValueChange={setLpId}>
            <SelectTrigger className="mt-1 w-44" data-testid="fund-doc-lp"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All LPs</SelectItem>{lps.map((lp) => <SelectItem key={lp.id} value={lp.id}>{lp.name} only</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={upload} disabled={uploading} className="gap-2" data-testid="fund-doc-upload-btn">{uploading && <Loader2 className="h-4 w-4 animate-spin" />} Upload</Button>
      </Card>
      {docs.length === 0 ? <Card className="rounded-xl border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="fund-docs-empty">No fund documents yet. Upload K-1s, the LPA and reports - LPs see them in their portal.</Card> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-lg border bg-card p-3 text-sm" data-testid={`fund-doc-${d.id}`}>
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{(CATS.find(([k]) => k === d.category) || [])[1] || "Other"} · {d.lp_name ? `${d.lp_name} only` : "All LPs"}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`fund-doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(d)} data-testid={`fund-doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FundDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [qr, setQr] = useState({ quarter: `Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()}`, commentary: "" });
  const [qrBusy, setQrBusy] = useState("");
  const load = () => api.get(`/funds/${id}/overview`).then(({ data }) => setData(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!data) return <AppShell title="Fund"><p className="text-sm text-muted-foreground">Loading…</p></AppShell>;
  const { fund, metrics } = data;

  const setStatus = async (status) => {
    try { await api.put(`/funds/${fund.id}`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadReport = async () => {
    setQrBusy("download");
    try {
      const res = await api.post(`/funds/${fund.id}/quarterly-report`, { ...qr, share: false }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `quarterly-report-${qr.quarter}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Report generation failed"); }
    finally { setQrBusy(""); }
  };

  const publishReport = async () => {
    setQrBusy("publish");
    try {
      await api.post(`/funds/${fund.id}/quarterly-report`, { ...qr, share: true });
      toast.success(`${qr.quarter} report published to every LP's document vault`);
      setQrOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setQrBusy(""); }
  };

  return (
    <AppShell
      title={fund.name}
      subtitle={`${String(fund.type).toUpperCase()}${fund.deal_name ? ` · ${fund.deal_name}` : ""}${fund.vintage_year ? ` · Vintage ${fund.vintage_year}` : ""} · ${fund.mgmt_fee_pct}% fee / ${fund.carry_pct}% carry / ${fund.hurdle_pct}% hurdle`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setQrOpen(true)} data-testid="quarterly-report-btn">
            <FileBarChart className="h-3.5 w-3.5" /> Quarterly report
          </Button>
          <Select value={fund.status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-36" data-testid="fund-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["raising", "active", "fully_deployed", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Link to="/app/funds"><Button variant="outline" size="sm" className="gap-1.5" data-testid="fund-back-btn"><ArrowLeft className="h-3.5 w-3.5" /> All vehicles</Button></Link>
        </div>
      }>
      <MetricCards m={metrics} />
      <Tabs defaultValue="lps" className="mt-6">
        <TabsList data-testid="fund-tabs">
          <TabsTrigger value="lps" data-testid="tab-lps">LPs</TabsTrigger>
          <TabsTrigger value="calls" data-testid="tab-calls">Capital calls</TabsTrigger>
          <TabsTrigger value="investments" data-testid="tab-investments">Investments</TabsTrigger>
          <TabsTrigger value="distributions" data-testid="tab-distributions">Distributions</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Fees & carry</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Documents</TabsTrigger>
        </TabsList>
        <TabsContent value="lps" className="mt-5"><LpsTab fund={fund} /></TabsContent>
        <TabsContent value="calls" className="mt-5"><CallsTab fund={fund} onChanged={load} /></TabsContent>
        <TabsContent value="investments" className="mt-5"><InvestmentsTab fund={fund} onChanged={load} /></TabsContent>
        <TabsContent value="distributions" className="mt-5"><DistributionsTab fund={fund} onChanged={load} /></TabsContent>
        <TabsContent value="fees" className="mt-5"><FeesCarryTab fund={fund} /></TabsContent>
        <TabsContent value="docs" className="mt-5"><DocsTab fund={fund} /></TabsContent>
      </Tabs>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-lg" data-testid="quarterly-report-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileBarChart className="h-5 w-5 text-primary" /> Quarterly LP report</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">One page: fund metrics, Schedule of Investments and your commentary. Download it, or publish straight into every LP's document vault.</p>
          <div className="space-y-3">
            <div><Label>Quarter</Label><Input value={qr.quarter} onChange={(e) => setQr((q) => ({ ...q, quarter: e.target.value }))} className="mt-1" data-testid="qr-quarter" /></div>
            <div><Label>Manager commentary (optional)</Label>
              <Textarea rows={4} value={qr.commentary} onChange={(e) => setQr((q) => ({ ...q, commentary: e.target.value }))}
                placeholder="Portfolio highlights, markups, pipeline, asks for LPs…" className="mt-1" data-testid="qr-commentary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={downloadReport} disabled={!!qrBusy} className="gap-2" data-testid="qr-download-btn">
              {qrBusy === "download" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </Button>
            <Button onClick={publishReport} disabled={!!qrBusy} className="gap-2" data-testid="qr-publish-btn">
              {qrBusy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish to LP vaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
